// DNA CONNECT - Perfil e Segurança
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { 
  User, Lock, Mail, Phone, Shield, Eye, EyeOff,
  Loader2, CheckCircle, AlertCircle, Pencil, X, Save
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = window.location.origin;

// Normaliza qualquer formato de erro (string, dict, array Pydantic) para uma mensagem legível
const extractErr = (data, fallback) => {
  if (!data) return fallback;
  const d = data.detail ?? data.message ?? data.error;
  if (!d) return fallback;
  if (typeof d === 'string') return d;
  if (Array.isArray(d)) {
    // Pydantic 422 → [{loc, msg, type}, ...]
    return d.map((e) => e?.msg || e?.message || JSON.stringify(e)).join(' · ');
  }
  if (typeof d === 'object') return d.message || d.msg || JSON.stringify(d);
  return fallback;
};

export default function DnaConnectPerfil() {
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [changingPassword, setChangingPassword] = useState(false);
  const [showPasswords, setShowPasswords] = useState({ current: false, new: false, confirm: false });
  const [passwordForm, setPasswordForm] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });
  const [savingPassword, setSavingPassword] = useState(false);
  
  // Estados para edição de perfil
  const [editingEmail, setEditingEmail] = useState(false);
  const [editingPhone, setEditingPhone] = useState(false);
  const [editEmail, setEditEmail] = useState('');
  const [editPhone, setEditPhone] = useState('');
  const [savingProfile, setSavingProfile] = useState(false);

  useEffect(() => {
    loadUser();
  }, []);

  const loadUser = async () => {
    try {
      const token = localStorage.getItem('familyToken');
      const response = await fetch(`${API_URL}/api/dnaconnect/auth/me?token=${token}`);
      const data = await response.json();
      setUser(data);
      setEditEmail(data.email || '');
      setEditPhone(data.responsible_phone || '');
    } catch (error) {
      toast.error('Erro ao carregar perfil');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateEmail = async () => {
    if (!editEmail || editEmail === user?.email) {
      setEditingEmail(false);
      return;
    }
    
    // Validar email
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(editEmail)) {
      toast.error('Email inválido');
      return;
    }
    
    setSavingProfile(true);
    try {
      const token = localStorage.getItem('familyToken');
      const response = await fetch(`${API_URL}/api/dnaconnect/auth/update-profile?token=${token}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: editEmail })
      });
      
      if (!response.ok) {
        const data = await response.json().catch(() => null);
        throw new Error(extractErr(data, 'Erro ao atualizar email'));
      }
      
      toast.success('Email atualizado com sucesso!');
      setEditingEmail(false);
      loadUser();
    } catch (error) {
      toast.error(error?.message || 'Erro ao atualizar email');
    } finally {
      setSavingProfile(false);
    }
  };

  const handleUpdatePhone = async () => {
    if (!editPhone || editPhone === user?.responsible_phone) {
      setEditingPhone(false);
      return;
    }
    
    setSavingProfile(true);
    try {
      const token = localStorage.getItem('familyToken');
      const response = await fetch(`${API_URL}/api/dnaconnect/auth/update-profile?token=${token}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone: editPhone })
      });
      
      if (!response.ok) {
        const data = await response.json().catch(() => null);
        throw new Error(extractErr(data, 'Erro ao atualizar telefone'));
      }
      
      toast.success('Telefone atualizado com sucesso!');
      setEditingPhone(false);
      loadUser();
    } catch (error) {
      toast.error(error?.message || 'Erro ao atualizar telefone');
    } finally {
      setSavingProfile(false);
    }
  };

  const formatPhone = (value) => {
    // Remove tudo que não é número
    const numbers = value.replace(/\D/g, '');
    
    // Formata conforme digita
    if (numbers.length <= 2) return `(${numbers}`;
    if (numbers.length <= 7) return `(${numbers.slice(0, 2)}) ${numbers.slice(2)}`;
    if (numbers.length <= 11) return `(${numbers.slice(0, 2)}) ${numbers.slice(2, 7)}-${numbers.slice(7)}`;
    return `(${numbers.slice(0, 2)}) ${numbers.slice(2, 7)}-${numbers.slice(7, 11)}`;
  };

  const handlePhoneChange = (e) => {
    const formatted = formatPhone(e.target.value);
    setEditPhone(formatted);
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (savingPassword) return; // impede clique duplo

    if (!passwordForm.current_password) {
      toast.error('Informe sua senha atual');
      return;
    }
    if (passwordForm.new_password !== passwordForm.confirm_password) {
      toast.error('As senhas não conferem');
      return;
    }

    if (passwordForm.new_password.length < 6) {
      toast.error('A nova senha deve ter no mínimo 6 caracteres');
      return;
    }

    setSavingPassword(true);
    try {
      const token = localStorage.getItem('familyToken');
      const response = await fetch(`${API_URL}/api/dnaconnect/auth/change-password?token=${token}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(passwordForm)
      });

      let data = null;
      try { data = await response.json(); } catch (_) { data = null; }

      if (!response.ok) {
        throw new Error(extractErr(data, 'Não foi possível alterar a senha.'));
      }

      toast.success(data?.message || 'Senha alterada com sucesso.');
      setChangingPassword(false);
      setPasswordForm({ current_password: '', new_password: '', confirm_password: '' });
    } catch (error) {
      toast.error(error?.message || 'Não foi possível alterar a senha.');
    } finally {
      setSavingPassword(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-2xl mx-auto">
      <h1 className="text-2xl font-bold text-gray-800">Perfil e Segurança</h1>

      {/* Dados do Usuário */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <User className="w-5 h-5" />
            Meus Dados
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center gap-4 p-4 bg-purple-50 rounded-xl">
            <div className="w-20 h-20 bg-purple-600 rounded-full flex items-center justify-center">
              <span className="text-3xl font-bold text-white">
                {user?.responsible_name?.charAt(0) || 'U'}
              </span>
            </div>
            <div>
              <h2 className="text-xl font-bold text-purple-900">{user?.responsible_name}</h2>
              <p className="text-purple-700">{user?.email}</p>
              {user?.responsible_phone && (
                <p className="text-purple-600 text-sm flex items-center gap-1 mt-1">
                  <Phone className="w-4 h-4" />
                  {user.responsible_phone}
                </p>
              )}
            </div>
          </div>

          <div className="grid gap-4">
            {/* Email - Editável */}
            <div className="p-3 bg-gray-50 rounded-lg">
              <div className="flex items-center justify-between mb-1">
                <div className="flex items-center gap-2 text-sm text-gray-500">
                  <Mail className="w-4 h-4" />
                  Email
                </div>
                {!editingEmail && (
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={() => setEditingEmail(true)}
                    className="h-7 text-purple-600 hover:text-purple-700"
                  >
                    <Pencil className="w-3 h-3 mr-1" />
                    Editar
                  </Button>
                )}
              </div>
              
              {editingEmail ? (
                <div className="flex gap-2 items-center">
                  <Input
                    type="email"
                    value={editEmail}
                    onChange={(e) => setEditEmail(e.target.value)}
                    className="flex-1"
                    placeholder="seu@email.com"
                    autoFocus
                  />
                  <Button 
                    size="sm" 
                    onClick={handleUpdateEmail}
                    disabled={savingProfile}
                    className="bg-green-600 hover:bg-green-700"
                  >
                    {savingProfile ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Save className="w-4 h-4" />
                    )}
                  </Button>
                  <Button 
                    size="sm" 
                    variant="ghost"
                    onClick={() => {
                      setEditingEmail(false);
                      setEditEmail(user?.email || '');
                    }}
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              ) : (
                <p className="font-medium">{user?.email}</p>
              )}
            </div>

            {/* Telefone - Editável */}
            <div className="p-3 bg-gray-50 rounded-lg">
              <div className="flex items-center justify-between mb-1">
                <div className="flex items-center gap-2 text-sm text-gray-500">
                  <Phone className="w-4 h-4" />
                  Celular / WhatsApp
                </div>
                {!editingPhone && (
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={() => setEditingPhone(true)}
                    className="h-7 text-purple-600 hover:text-purple-700"
                  >
                    <Pencil className="w-3 h-3 mr-1" />
                    Editar
                  </Button>
                )}
              </div>
              
              {editingPhone ? (
                <div className="flex gap-2 items-center">
                  <Input
                    type="tel"
                    value={editPhone}
                    onChange={handlePhoneChange}
                    className="flex-1"
                    placeholder="(11) 99999-9999"
                    maxLength={15}
                    autoFocus
                  />
                  <Button 
                    size="sm" 
                    onClick={handleUpdatePhone}
                    disabled={savingProfile}
                    className="bg-green-600 hover:bg-green-700"
                  >
                    {savingProfile ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Save className="w-4 h-4" />
                    )}
                  </Button>
                  <Button 
                    size="sm" 
                    variant="ghost"
                    onClick={() => {
                      setEditingPhone(false);
                      setEditPhone(user?.responsible_phone || '');
                    }}
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              ) : (
                <p className="font-medium">{user?.responsible_phone || 'Não informado'}</p>
              )}
            </div>

            <div className="p-3 bg-gray-50 rounded-lg">
              <div className="flex items-center gap-2 text-sm text-gray-500 mb-1">
                <User className="w-4 h-4" />
                Filhos Vinculados
              </div>
              <div className="flex flex-wrap gap-2 mt-1">
                {user?.students?.map((student) => (
                  <span key={student.id} className="px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-sm">
                    {student.name?.split(' ')[0]}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Alterar Senha */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Lock className="w-5 h-5" />
            Segurança
          </CardTitle>
        </CardHeader>
        <CardContent>
          {!changingPassword ? (
            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                  <Shield className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="font-medium">Senha</p>
                  <p className="text-sm text-gray-500">••••••••</p>
                </div>
              </div>
              <Button variant="outline" onClick={() => setChangingPassword(true)}>
                Alterar Senha
              </Button>
            </div>
          ) : (
            <form onSubmit={handleChangePassword} className="space-y-4">
              <div>
                <Label htmlFor="current">Senha Atual</Label>
                <div className="relative">
                  <Input
                    id="current"
                    type={showPasswords.current ? 'text' : 'password'}
                    value={passwordForm.current_password}
                    onChange={(e) => setPasswordForm(prev => ({ ...prev, current_password: e.target.value }))}
                    className="pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPasswords(prev => ({ ...prev, current: !prev.current }))}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400"
                  >
                    {showPasswords.current ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <div>
                <Label htmlFor="new">Nova Senha</Label>
                <div className="relative">
                  <Input
                    id="new"
                    type={showPasswords.new ? 'text' : 'password'}
                    value={passwordForm.new_password}
                    onChange={(e) => setPasswordForm(prev => ({ ...prev, new_password: e.target.value }))}
                    className="pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPasswords(prev => ({ ...prev, new: !prev.new }))}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400"
                  >
                    {showPasswords.new ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <div>
                <Label htmlFor="confirm">Confirmar Nova Senha</Label>
                <div className="relative">
                  <Input
                    id="confirm"
                    type={showPasswords.confirm ? 'text' : 'password'}
                    value={passwordForm.confirm_password}
                    onChange={(e) => setPasswordForm(prev => ({ ...prev, confirm_password: e.target.value }))}
                    className="pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPasswords(prev => ({ ...prev, confirm: !prev.confirm }))}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400"
                  >
                    {showPasswords.confirm ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <div className="flex gap-2">
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={() => {
                    setChangingPassword(false);
                    setPasswordForm({ current_password: '', new_password: '', confirm_password: '' });
                  }}
                >
                  Cancelar
                </Button>
                <Button type="submit" disabled={savingPassword}>
                  {savingPassword ? (
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  ) : (
                    <CheckCircle className="w-4 h-4 mr-2" />
                  )}
                  Salvar Nova Senha
                </Button>
              </div>
            </form>
          )}
        </CardContent>
      </Card>

      {/* Info de Privacidade */}
      <Card className="border-blue-200 bg-blue-50">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-medium text-blue-900">Privacidade e Segurança</p>
              <p className="text-sm text-blue-700 mt-1">
                Seus dados são protegidos e tratados de acordo com a LGPD. 
                Nunca compartilhe sua senha com terceiros.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
